import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';

import Footer from "../../Footer/FooterBottom";
import {
    fetchFileManager,
    removeFileManager,
    undoDeleteFileManager,
    clearFileManager
} from './actions';
import ListItem from "./ListItem";
import UploadFileForm from "./Forms/Upload";
import NameForm from "./Forms/NameForm";
import api from '../../../api/api';
import './filemanager.css';

const FileManagerList = () => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();

    // Redux selectors
    const fileManager = useSelector(state => state.fileManager);
    
    // Local state
    const [search, setSearch] = useState("");
    const [path, setPath] = useState("");
    const [viewType, setViewType] = useState("grid");
    const [showEditFormModal, setShowEditFormModal] = useState(false);
    const [newFolder, setNewFolder] = useState(false);

    // Initial sync
    useEffect(() => {
        dispatch(fetchFileManager(path));
    }, [dispatch, path]);

    const changeView = () => {
        setViewType(prev => prev === "list" ? "grid" : "list");
    };

    const handleOpenNewFolderModal = () => {
        setNewFolder(true);
    };

    const openFolder = (newPath) => {
        const p = newPath || "";
        setPath(p);
    };

    const Sync = (p) => {
        dispatch(fetchFileManager(p || ""));
    };

    const ShowEditForm = () => {
        setShowEditFormModal(true);
    };

    const handleClose = () => {
        setShowEditFormModal(false);
        setNewFolder(false);
    };

    // Breadcrumbs
    const renderBreadcrumbs = () => {
        if (!path || path === "/") {
            return (
                <li className="breadcrumb-item active text-dark" aria-current="page" style={{ fontSize: '15px' }}>
                    <i className="fas fa-home mr-1"></i> {t("Pages.FileManager.root")}
                </li>
            );
        }

        const parts = path.split("/").filter(Boolean);
        const breadcrumbs = [];
        let currentPath = "";

        breadcrumbs.push(
            <li key="root" className="breadcrumb-item">
                <span 
                    className="text-primary cursor-pointer font-weight-medium hover-underline" 
                    onClick={() => openFolder("")}
                    style={{ cursor: 'pointer' }}
                >
                    <i className="fas fa-home mr-1"></i> {t("Pages.FileManager.root")}
                </span>
            </li>
        );

        parts.forEach((part, index) => {
            currentPath += part + "/";
            const isLast = index === parts.length - 1;
            if (isLast) {
                breadcrumbs.push(
                    <li key={currentPath} className="breadcrumb-item active text-dark" aria-current="page">
                        {part}
                    </li>
                );
            } else {
                const targetPath = currentPath;
                breadcrumbs.push(
                    <li key={currentPath} className="breadcrumb-item">
                        <span 
                            className="text-primary cursor-pointer font-weight-medium hover-underline" 
                            onClick={() => openFolder(targetPath)}
                            style={{ cursor: 'pointer' }}
                        >
                            {part}
                        </span>
                    </li>
                );
            }
        });

        return breadcrumbs;
    };

    // Render list
    const renderFileManagerContent = () => {
        let isLoaded = fileManager ? fileManager.isLoaded : false;
        let toRemove = fileManager ? fileManager.toRemove : [];

        if (fileManager && fileManager.files && fileManager.files.length > 0) {
            let filterFiles = fileManager.files.filter(v => 
                v && v.attributes && v.attributes.name && v.attributes.name.includes(search)
            );

            if (isLoaded === false) {
                return (
                    <div className="product-list-item w-100 text-center py-5">
                        <div className="display-1 text-danger">
                            <i className="fad fa-sync-alt fa-spin"></i>
                        </div>
                        <h4 className="subtitle mt-3">
                            {t("Pages.FileManager.Loading")}
                        </h4>
                    </div>
                );
            }

            if (filterFiles.length === 0 && search) {
                return (
                    <div className="product-list-item w-100 text-center py-5">
                        <div className="display-1 text-danger">
                            <i className="fad fa-telescope animate-bounce"></i>
                        </div>
                        <h4 className="subtitle mt-3">
                            {t("Pages.FileManager.NotFound")}
                        </h4>
                        <div className="h4 text-danger mt-2">
                            "{search}" {t("Pages.FileManager.IsNotHere")}
                        </div>
                    </div>
                );
            }

            return filterFiles.map(file => {
                if (!file) return null;
                return (toRemove && toRemove.includes(file.id)) ? null : (
                    <ListItem
                        key={file.id}
                        item={file}
                        Sync={Sync}
                        path={path}
                        viewType={viewType}
                        openFolder={openFolder}
                        remove={showDelete}
                    />
                );
            });
        }

        if (fileManager && fileManager.isLoaded === false) {
            return (
                <div className="product-list-item w-100 text-center py-5">
                    <div className="display-1 text-danger">
                        <i className="fad fa-sync-alt fa-spin"></i>
                    </div>
                    <h4 className="subtitle mt-3">
                        {t("Pages.FileManager.Loading")}
                    </h4>
                </div>
            );
        }

        return (
            <div className="product-list-item w-100 text-center py-5">
                <div className="display-1 text-danger">
                    <i className="fad fa-telescope"></i>
                </div>
                <h4 className="subtitle mt-3">
                    {t("Pages.FileManager.NotFound")}
                </h4>
            </div>
        );
    };

    // Actions
    const showDelete = (item) => {
        if (item && item.id) {
            dispatch(removeFileManager(item.id));

            let _toastId = toast(t('Processing'), {
                position: "bottom-left",
                autoClose: false
            });

            toast.update(_toastId, {
                render: () => (
                    <div>
                        <i className="fa-spin fas fa-spinner mr-2"></i> {t('DeleteProcessing')}
                        <div className="row row-cols-2 mt-2">
                            <div className="col">
                                <button type="button" className="btn btn-danger btn-sm btn-block" onClick={() => { undoDelete(item); toast.dismiss(_toastId); }}>
                                    <i className="icon fal fa-trash-undo"></i> {t('undo')}
                                </button>
                            </div>
                            <div className="col">
                                <button type="button" className="btn btn-light btn-sm btn-block" onClick={() => { deleteFiles(item); toast.dismiss(_toastId); }}>
                                    <i className="icon fal fa-recycle"></i> {t('confirm')}
                                </button>
                            </div>
                        </div>
                    </div>
                ),
                type: toast.TYPE.DARK,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true
            });
        }
    };

    const undoDelete = (item) => {
        if (item && item.id) {
            dispatch(undoDeleteFileManager(item.id));
        }
    };

    const deleteFiles = (item) => {
        if (!item || !item.id) return;

        let _toastId = toast(t('Processing'), {
            position: "bottom-left",
            autoClose: false
        });
        toast.update(_toastId, {
            render: () => <div> <i className="fa-spin fas fa-spinner"></i> {t('Processing')} </div>,
            autoClose: false,
            closeOnClick: false,
        });

        api.delete(`/FileManager/?path=${item.id}`).then(response => {
            dispatch(clearFileManager(item.id));
            Sync(path);

            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-exclamation-triangle"></i> {t('Pages.FileManager.toast_Delete.Title')}</div> <div className="h6">{t('Pages.FileManager.toast_Delete.Body')}</div></div>,
                type: toast.TYPE.SUCCESS,
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });

        }).catch(error => {
            console.error("Delete files error:", error);
            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        });
    };

    const onSubmit = (postData, _toastId) => {
        api.post(`/FileManager/`, postData).then(response => {
            Sync(path);

            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.FileManager.add_success_toast.Title')}</div> <div className="h6">{t('Pages.FileManager.add_success_toast.Body')}</div></div>,
                type: toast.TYPE.SUCCESS,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });

            setShowEditFormModal(false);

        }).catch(error => {
            console.error("Submit error:", error);
            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        });
    };

    const onSubmitAddFolder = (postData, _toastId) => {
        api.post(`/FileManager/?path=${path}&isFolder=true`, postData).then(response => {
            Sync(path);

            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.FileManager.add_folder_success_toast.Title')}</div> <div className="h6">{t('Pages.FileManager.add_folder_success_toast.Body')}</div></div>,
                type: toast.TYPE.SUCCESS,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });

            setNewFolder(false);

        }).catch(error => {
            console.error("Add folder error:", error);
            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        });
    };

    const isLoaded = fileManager ? fileManager.isLoaded : false;
    const splitPath = path.split("/").filter(Boolean);
    const parentPath = splitPath.slice(0, splitPath.length - 1).join("/") + (splitPath.length > 1 ? "/" : "");

    return (
        <>
            {newFolder && (
                <Modal
                    className="file-manager-page"
                    size="md"
                    show={newFolder}
                    onHide={handleClose}
                    backdrop="static"
                    keyboard={false}
                    enforceFocus={false} >
                    <Modal.Header closeButton>
                        <Modal.Title> <i className="icon fal fa-folder-plus"></i> {t('Pages.FileManager.newFolder')} </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category">
                            <div className="widget-body">
                                <NameForm
                                    path={path}
                                    onSubmit={onSubmitAddFolder}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
            
            {showEditFormModal && (
                <Modal
                    className="file-manager-page"
                    size="xl"
                    show={showEditFormModal}
                    onHide={handleClose}
                    backdrop="static"
                    keyboard={false}
                    enforceFocus={false} >
                    <Modal.Header closeButton>
                        <Modal.Title> <i className="icon fal fa-upload"></i> {t('Pages.FileManager.Add')} </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category">
                            <div className="widget-body">
                                <UploadFileForm
                                    path={path}
                                    onSubmit={onSubmit}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            <section className="file-manager-page pos-rel padding-top padding-bottom">
                <div className="top-shape-center">
                    <LazyLoadImage src="/assets/css/img/gallery1.png" alt={`${t('alarabeyya')}`} />
                </div>
                <div className="bottom-shape-left">
                    <LazyLoadImage src="/assets/css/img/bottom-shape.png" alt={`${t('alarabeyya')}`} />
                </div>
                <div className="container-fluid px-lg-5">
                    <Helmet>
                        <title>{t("Pages.FileManager.title")} | {t('alarabeyya')}</title>
                        <script src={process.env.PUBLIC_URL + '/assets/redactor/redactor.js?v=' + process.env.REACT_APP_VERSION} ></script>
                    </Helmet>

                    <div className="row justify-content-center mb-4">
                        <div className="col-12">
                            <div className="section-header text-center mb-4">
                                <h3 className="title font-weight-bold"> {t('Pages.FileManager.title')} </h3>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            {/* Modern Toolbar card */}
                            <div className="card shadow-sm border rounded p-3 mb-4 bg-white">
                                <div className="row align-items-center">
                                    <div className="col-md-6 mb-3 mb-md-0 text-right">
                                        <nav aria-label="breadcrumb">
                                            <ol className="breadcrumb bg-transparent p-0 m-0 d-flex flex-wrap" style={{ direction: 'rtl' }}>
                                                {renderBreadcrumbs()}
                                            </ol>
                                        </nav>
                                    </div>
                                    <div className="col-md-6 d-flex justify-content-md-end align-items-center flex-wrap" style={{ direction: 'rtl' }}>
                                        <div className="d-flex align-items-center flex-wrap">
                                            <button onClick={() => openFolder("")} title={t("Pages.FileManager.root")} className="btn btn-outline-dark btn-sm m-1">
                                                <i className="fas fa-home"></i>
                                            </button>
                                            {path && path !== "/" && (
                                                <button onClick={() => openFolder(parentPath)} title={t("Pages.FileManager.upFolder")} className="btn btn-outline-warning btn-sm m-1">
                                                    <i className="fas fa-level-up"></i>
                                                </button>
                                            )}
                                            <div className="border-left mx-2" style={{ height: '24px' }}></div>
                                            
                                            <button onClick={handleOpenNewFolderModal} title={t("Pages.FileManager.newFolder")} className="btn btn-info btn-sm m-1 text-white">
                                                <i className="fas fa-folder-plus mr-1"></i> {t("Pages.FileManager.newFolder")}
                                            </button>
                                            <button onClick={ShowEditForm} title={t("Pages.FileManager.Add")} className="btn btn-primary btn-sm m-1">
                                                <i className="fas fa-upload mr-1"></i> {t("Pages.FileManager.Add")}
                                            </button>
                                            <button onClick={() => Sync(path)} title={t("Pages.FileManager.Sync")} className="btn btn-danger btn-sm m-1">
                                                {isLoaded ? <i className="fas fa-sync-alt"></i> : <i className="fas fa-sync-alt fa-spin"></i>}
                                            </button>
                                            <button onClick={changeView} title={t("Pages.FileManager.changeView")} className="btn btn-success btn-sm m-1">
                                                {viewType === "list" ? <i className="fas fa-th"></i> : <i className="fas fa-list"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modern Search box */}
                            <div className="card shadow-sm border rounded p-3 mb-4 bg-light">
                                <div className="d-flex align-items-center">
                                    <div className="position-relative flex-grow-1">
                                        <input 
                                            type="text" 
                                            className="form-control text-right" 
                                            onChange={e => setSearch(e.target.value)} 
                                            value={search} 
                                            placeholder={t("Pages.FileManager.search")} 
                                            style={{ height: '45px', borderRadius: '8px', paddingRight: '45px' }}
                                        />
                                        <div className="position-absolute" style={{ top: '13px', right: '15px' }}>
                                            <i className="fas fa-search text-muted"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Product List wrap */}
                            <div className={`shop-product-wrap row mb-30-none ${viewType}`}>
                                {renderFileManagerContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer></Footer>
        </>
    );
};

export default FileManagerList;